"use client"

import { useState, useEffect } from "react"
import { DiscordLogo, X, Loader2, Crown, Zap, Percent, Shield, Star, Gift, Sparkles } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { useFeatureFlag } from "@/lib/feature-flags"
import { useAdStore } from "@/lib/ad-store"
import { useRouter } from "next/navigation"
import { toast } from "sonner"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { useModalSlot } from "@/lib/client/modal-coordinator"
import { usePatreonMode } from "@/lib/use-patreon-mode"
import { PATREON_SUBSCRIBE_PATH } from "@/lib/patreon/paths"

export interface PromoCampaign {
  id: string
  name: string
  title: string
  subtitle?: string | null
  image_url?: string | null
  badge_label?: string | null
  badge_color: string
  perks: string[]
  cta_text: string
  cta_type: "founder_checkout" | "url" | "pricing"
  cta_url?: string | null
  secondary_text?: string | null
  slot: string
  active: boolean
}

const ACCENT_CLASSES: Record<string, { bar: string; badge: string; btn: string; icon: string; perk: string }> = {
  amber:  { bar: "from-amber-500 via-purple-500 to-amber-500", badge: "bg-amber-500/20 border-amber-500/40 text-amber-400", btn: "from-amber-500 to-purple-600 hover:from-amber-400 hover:to-purple-500", icon: "text-amber-400", perk: "text-amber-400" },
  purple: { bar: "from-purple-500 via-primary to-purple-500",  badge: "bg-purple-500/20 border-purple-500/40 text-purple-400", btn: "from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500", icon: "text-purple-400", perk: "text-purple-400" },
  orange: { bar: "from-orange-500 via-red-500 to-orange-500",  badge: "bg-orange-500/20 border-orange-500/40 text-orange-400", btn: "from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400", icon: "text-orange-400", perk: "text-orange-400" },
  red:    { bar: "from-red-500 via-pink-500 to-red-500",       badge: "bg-red-500/20 border-red-500/40 text-red-400",    btn: "from-red-500 to-pink-600 hover:from-red-400 hover:to-pink-500", icon: "text-red-400", perk: "text-red-400" },
  green:  { bar: "from-green-500 via-emerald-500 to-green-500",badge: "bg-green-500/20 border-green-500/40 text-green-400",btn: "from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500", icon: "text-green-400", perk: "text-green-400" },
  blue:   { bar: "from-blue-500 via-indigo-500 to-blue-500",   badge: "bg-blue-500/20 border-blue-500/40 text-blue-400",  btn: "from-blue-500 to-indigo-600 hover:from-blue-400 hover:to-indigo-500", icon: "text-blue-400", perk: "text-blue-400" },
}

const PERK_ICONS = [Crown, Percent, DiscordLogo, Zap, Shield, Star, Gift, Sparkles]

const SESSION_KEY = "promo_modal_dismissed"
const DELAY_MS = 25000

interface PromoModalProps {
  featureFlag?: string
}

export function PromoModal({ featureFlag = "promo_modal_enabled" }: PromoModalProps) {
  const t = useTranslations("promoModal")
  const [campaign, setCampaign] = useState<PromoCampaign | null>(null)
  const [visible, setVisible] = useState(false)
  const [acting, setActing] = useState(false)
  const { enabled: flagEnabled, loading: flagLoading } = useFeatureFlag(featureFlag)
  const { isPremiumUser } = useAdStore()
  const router = useRouter()
  const { enabled: patreonMode } = usePatreonMode()

  useEffect(() => {
    fetch("/api/promo-campaigns/active?slot=main")
      .then(r => r.json())
      .then(d => { if (d.campaign) setCampaign(d.campaign) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (flagLoading || !flagEnabled || isPremiumUser || !campaign) return
    try { if (sessionStorage.getItem(SESSION_KEY)) return } catch {}
    const timer = setTimeout(() => setVisible(true), DELAY_MS)
    return () => clearTimeout(timer)
  }, [flagLoading, flagEnabled, isPremiumUser, campaign])

  const dismiss = () => {
    setVisible(false)
    try { sessionStorage.setItem(SESSION_KEY, "1") } catch {}
  }

  const handleCTA = async () => {
    if (!campaign) return
    if (campaign.cta_type === "founder_checkout" && patreonMode) {
      // Patreon mode: Founder is bought on Patreon; the subscribe page explains it.
      dismiss()
      router.push(PATREON_SUBSCRIBE_PATH)
    } else if (campaign.cta_type === "founder_checkout") {
      setActing(true)
      try {
        const res = await fetch("/api/subscriptions/founder-checkout", { method: "POST" })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error)
        window.location.href = data.url
      } catch (err: any) {
        toast.error(err.message || t("somethingWentWrong"))
        setActing(false)
      }
    } else if (campaign.cta_type === "pricing") {
      dismiss()
      router.push("/subscribe")
    } else if (campaign.cta_url) {
      dismiss()
      if (campaign.cta_url.startsWith("http")) {
        window.open(campaign.cta_url, "_blank")
      } else {
        router.push(campaign.cta_url)
      }
    }
  }

  const canShow = useModalSlot("promo", visible && !!campaign)

  if (!visible || !campaign || !canShow) return null

  const accent = ACCENT_CLASSES[campaign.badge_color] ?? ACCENT_CLASSES.amber
  const perks: string[] = Array.isArray(campaign.perks) ? campaign.perks : []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-sm bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Accent bar */}
        <div className={`h-1 w-full bg-gradient-to-r ${accent.bar}`} />

        <button onClick={dismiss} className="absolute top-3 right-3 z-10 text-muted-foreground hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
        </button>

        {/* Art header */}
        {campaign.image_url ? (
          <div className="relative h-40 w-full overflow-hidden bg-gradient-to-br from-purple-950/40 to-black/60">
            <Image
              src={campaign.image_url}
              alt=""
              fill
              className="object-contain object-right-bottom scale-110 pointer-events-none select-none"
              priority
              onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = "none" }}
            />
            <div className="absolute inset-0 flex flex-col justify-end p-4 bg-gradient-to-r from-background/85 via-background/50 to-transparent">
              {campaign.badge_label && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border w-fit mb-1 ${accent.badge}`}>
                  <Crown className="h-2.5 w-2.5" />
                  {campaign.badge_label}
                </span>
              )}
              <h2 className="font-bold text-base leading-tight">{campaign.title}</h2>
              {campaign.subtitle && <p className="text-xs text-muted-foreground mt-0.5">{campaign.subtitle}</p>}
            </div>
          </div>
        ) : (
          <div className="p-5 pb-0">
            {campaign.badge_label && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border w-fit mb-2 ${accent.badge}`}>
                <Crown className="h-2.5 w-2.5" />
                {campaign.badge_label}
              </span>
            )}
            <h2 className="font-bold text-base">{campaign.title}</h2>
            {campaign.subtitle && <p className="text-xs text-muted-foreground mt-0.5">{campaign.subtitle}</p>}
          </div>
        )}

        <div className="p-5 space-y-4">
          {/* Perks */}
          {perks.length > 0 && (
            <ul className="space-y-1.5">
              {perks.map((perk, i) => {
                const Icon = PERK_ICONS[i % PERK_ICONS.length]
                return (
                  <li key={i} className="flex items-start gap-2">
                    <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${accent.perk}`} />
                    <span className="text-xs text-muted-foreground">{perk}</span>
                  </li>
                )
              })}
            </ul>
          )}

          {/* CTA */}
          <Button
            onClick={handleCTA}
            disabled={acting}
            className={`w-full bg-gradient-to-r text-white border-0 ${accent.btn}`}
          >
            {acting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("redirecting")}</> : campaign.cta_text}
          </Button>

          {campaign.secondary_text && (
            <button onClick={dismiss} className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors">
              {campaign.secondary_text}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
